import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Input } from '@rallyshift/design-system'
import api from '../../api/client'
import { ROOT_DOMAIN, isPlausibleSlug } from '../../api/tenant'

const PlatformImportPage: React.FC = () => {
  const navigate = useNavigate()
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [document, setDocument] = useState<unknown>(null)
  const [fileName, setFileName] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const onFile = async (file: File | null) => {
    setError('')
    setDocument(null)
    setFileName(file?.name || '')
    if (!file) return
    try {
      const parsed = JSON.parse(await file.text())
      setDocument(parsed)
      if (!slug && typeof parsed?.organization?.slug === 'string') {
        setSlug(`${parsed.organization.slug}-kopie`)
      }
      if (!name && typeof parsed?.organization?.name === 'string') {
        setName(`${parsed.organization.name} (kopie)`)
      }
    } catch {
      setError('Dat bestand is geen geldige JSON-export.')
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const candidate = slug.trim().toLowerCase()
    if (!isPlausibleSlug(candidate)) {
      setError('Het webadres mag alleen kleine letters, cijfers en streepjes bevatten.')
      return
    }
    if (!document) {
      setError('Kies eerst een exportbestand.')
      return
    }

    setSaving(true)
    setError('')
    try {
      const report = await api.platform.importOrganization({
        slug: candidate,
        name: name.trim() || undefined,
        data: document,
      })
      navigate(`/platform/organizations/${report.organization.id}`, { replace: true })
    } catch (err: any) {
      setError(err.message || 'Importeren is niet gelukt.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-display-md text-text-primary">Organisatie importeren</h1>
        <p className="mt-1 text-md text-text-secondary">
          Altijd als een nieuwe organisatie, nooit in een bestaande. Als het misgaat,
          verwijder je de kopie.
        </p>
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-col gap-1.5 font-body">
            <label htmlFor="export-file" className="text-sm font-semibold text-text-primary">
              JSON-export
            </label>
            <input
              id="export-file"
              type="file"
              accept="application/json,.json"
              onChange={(e) => onFile(e.target.files?.[0] || null)}
            />
            {fileName && <span className="text-[13px] text-text-muted">{fileName}</span>}
          </div>
          <Input
            label="Nieuw webadres"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            hint={`${slug.trim().toLowerCase() || 'kopie'}.${ROOT_DOMAIN}`}
            required
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <Input
            label="Naam (optioneel)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint="Leeg laten om de naam uit het bestand te houden."
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" loading={saving} disabled={!document}>
              Importeren
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/platform')}>
              Annuleren
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

export default PlatformImportPage
